import { FunctionComponent, MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { faBan, faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { Icon } from "@/components";
import { AssignStatus } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
	feedSelector,
	setFeedSelectedCardId,
	setFeedSelectedPerson,
} from "@/pages/Private/redux/feed/feed.slice";
import { getSignalPriorityLabel } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/SignalTimeline/signalTimelineUtils";

import { feedCardKey } from "../../utils/feedCardKey";

import { ReachOutButton } from "./ReachOutButton";
import { RunCardActionsMenu } from "./RunCardActionsMenu";
import { RunEntityHeader } from "./RunEntityHeader";
import { SignalRow } from "./SignalRow";
import { SPINE_CLASS } from "./runCardUtils";

/** Collapsed cards show the strongest five signals (server-sorted) — D8 of the plan. */
const TOP_SIGNAL_COUNT = 5;

/**
 * Interactive controls inside the card mark themselves with `data-feed-noselect`
 * so a click on them never doubles as card selection (mockup `bindFeed` guard, D6).
 */
const isGuardedClick = (event: MouseEvent): boolean =>
	event.target instanceof Element && !!event.target.closest("[data-feed-noselect]");

/**
 * Border + ring of the card. The jump flash (D3) is the selection ring, widened: a ring is a
 * box-shadow, which transitions, so it eases back down to the resting selection ring on its own
 * when the flash clears — no keyframes, no extra colour token.
 */
const getHighlightClass = (isSelected: boolean, isFlashing: boolean): string => {
	if (isFlashing) {
		return "border-brand-light ring-4 ring-brand-light";
	}

	if (isSelected) {
		return "border-brand-light ring-1 ring-brand-light";
	}

	return "border-border-light";
};

interface RunCardProps {
	card: FeedRunCard;
	/** Ignore-this-time (D3) — the page shell owns the mutation + undo toast. */
	onIgnore?: (card: FeedRunCard) => void;
	/** Overflow-menu lead-status change (Review later / Back to New / Ignored) — page-owned. */
	onChangeStatus?: (card: FeedRunCard, person: FeedPerson, status: AssignStatus) => void;
	/** Overflow-menu "Remove person" — the page owns the tracker mutation + toast. */
	onRemovePerson?: (card: FeedRunCard, person: FeedPerson) => Promise<void>;
	/** Overflow-menu "Stop tracking company" — the page owns the mutation + toast. */
	onRemoveCompany?: (card: FeedRunCard) => Promise<void>;
	/** Registers the card's root node so a jump-to-company pick can scroll to it (D3). */
	cardRef?: (element: HTMLDivElement | null) => void;
	/** True while the Feed is fetching — an add must not read that fetch as its answer. */
	isFeedFetching?: boolean;
	/** Briefly widens the selection ring right after a jump landed on this card (D3). */
	isFlashing?: boolean;
}

/**
 * One company-run card (PREC-3635 P5): relationship spine, prio label, entity
 * header, run title + clamped summary, top-5 signal rows with show-more and the
 * footer (ignore-this-time + the P6 reach-out slot). Card click selects the card
 * for the docked panel (D6); guarded controls don't.
 */
export const RunCard: FunctionComponent<RunCardProps> = ({
	card,
	onIgnore,
	onChangeStatus,
	onRemovePerson,
	onRemoveCompany,
	cardRef,
	isFeedFetching = false,
	isFlashing = false,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { selectedCardId, selectedPersonByRunId } = useAppSelector(feedSelector);

	const [showAllSignals, setShowAllSignals] = useState(false);
	const [expandedSignalId, setExpandedSignalId] = useState<number | null>(null);

	const cardKey = feedCardKey(card);
	const selectedPerson =
		card.people.find((person) => person.listPersonId === selectedPersonByRunId[cardKey]) ??
		card.people[0] ??
		null;

	const isSelected = selectedCardId === cardKey;
	// "Ignore this time" stamps a run — a runless card (PREC-3584) has none, so hide it.
	const canIgnore = card.runId !== null;
	const isStrong = getSignalPriorityLabel(card.strongestPriority) === "Priority 1";
	const hiddenSignalCount = card.signals.length - TOP_SIGNAL_COUNT;
	const visibleSignals = showAllSignals ? card.signals : card.signals.slice(0, TOP_SIGNAL_COUNT);

	const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
		// Clicks bubbling in from portaled UI (identity-card modals/menus live in
		// `document.body`) are not card clicks — only in-card, unguarded ones are.
		if (!event.currentTarget.contains(event.target as Node) || isGuardedClick(event)) {
			return;
		}

		dispatch(setFeedSelectedCardId(cardKey));
	};

	return (
		<div
			ref={cardRef}
			className={classNames(
				"relative cursor-pointer overflow-hidden rounded-xl border bg-white transition-[border-color,box-shadow] duration-300",
				getHighlightClass(isSelected, isFlashing)
			)}
			data-testid={`feed-run-card-${cardKey}`}
			onClick={handleCardClick}
		>
			<div className={classNames("absolute bottom-0 left-0 top-0 w-1", SPINE_CLASS[card.spine])} />

			<div className="flex flex-col p-4 pl-5">
				{card.strongestPriority && (
					<div className="mb-3">
						<span
							className={classNames(
								"inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold",
								isStrong
									? "bg-background-success text-text-success"
									: "bg-background-warning text-text-warning"
							)}
							data-testid="feed-run-prio-label"
						>
							<span>{t("feed.runCard.prioTier", { tier: card.strongestPriority })}</span>
							<span>–</span>
							<span className="font-medium">
								{isStrong ? t("feed.runCard.prioMeaningStrong") : t("feed.runCard.prioMeaningSoft")}
							</span>
						</span>
					</div>
				)}

				<RunEntityHeader
					card={card}
					isFeedFetching={isFeedFetching}
					selectedPerson={selectedPerson}
					onSelectPerson={(listPersonId) =>
						dispatch(setFeedSelectedPerson({ runId: cardKey, listPersonId }))
					}
				/>

				{card.title && (
					<h3 className="mb-1.5 mt-3.5 text-base font-bold leading-snug text-text">{card.title}</h3>
				)}

				{card.summaryWhat && (
					<p
						className="mb-4 text-[13px] leading-relaxed text-text-secondary line-clamp-2"
						data-testid="feed-run-summary"
					>
						<span>{card.summaryWhat}</span>
						{/* Sentence 2 is null on the account's first run — omit, don't render an empty span. */}
						{card.summaryRelation && <span> {card.summaryRelation}</span>}
					</p>
				)}

				<div className="mb-2 pl-1 text-[11px] font-bold uppercase tracking-wide text-text-tertiary">
					{t("feed.runCard.evidenceSince", {
						date: format(new Date(card.runAt), "d MMM yyyy"),
					})}
				</div>

				<div className="flex flex-col">
					{visibleSignals.map((signal) => (
						<SignalRow
							key={signal.signalId}
							card={card}
							isExpanded={expandedSignalId === signal.signalId}
							selectedPerson={selectedPerson}
							signal={signal}
							onToggleExpand={() =>
								setExpandedSignalId((current) =>
									current === signal.signalId ? null : signal.signalId
								)
							}
						/>
					))}
				</div>

				{hiddenSignalCount > 0 && (
					<button
						className="mt-1 flex cursor-pointer items-center gap-1.5 self-start rounded-md px-2 py-1 text-xs font-semibold text-text-secondary hover:bg-background-light"
						data-feed-noselect="true"
						data-testid="feed-show-more-signals"
						type="button"
						onClick={() => setShowAllSignals((show) => !show)}
					>
						{showAllSignals
							? t("feed.runCard.showFewer")
							: t("feed.runCard.showMore", { count: hiddenSignalCount })}
						<Icon
							className={classNames(
								"h-3 w-3 transition-transform",
								showAllSignals ? "rotate-180" : ""
							)}
							icon={faChevronDown}
						/>
					</button>
				)}

				<div className="mt-3 flex items-center gap-2">
					{canIgnore && (
						<button
							className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-border-light px-3 py-1.5 text-xs font-semibold text-status-error hover:bg-status-error-light disabled:cursor-not-allowed disabled:opacity-60"
							data-feed-noselect="true"
							data-testid="feed-ignore-run"
							type="button"
							onClick={() => onIgnore?.(card)}
						>
							<Icon className="h-3 w-3" icon={faBan} />
							{t("feed.runCard.ignoreRun")}
						</button>
					)}
					{/* P6 ReachOutButton (D3) — empty on degraded cards (P5 D7). */}
					<div className="ml-auto" data-feed-noselect="true" data-testid="feed-reach-out-slot">
						<ReachOutButton card={card} person={selectedPerson} />
					</div>
					{/* Footer (…) overflow — card-level actions with no dedicated button. */}
					<div data-feed-noselect="true">
						<RunCardActionsMenu
							card={card}
							person={selectedPerson}
							onChangeStatus={onChangeStatus}
							onRemoveCompany={onRemoveCompany}
							onRemovePerson={onRemovePerson}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
